import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogCloseButton,
  Button,
  BUTTON_VARIANTS,
  Toast,
  TOAST_VARIANTS,
} from 'cdk-radial';
import {
  getPlanName,
  getPlanType,
  getPlansToShowForAPI,
} from '../../../utils/apiPricingUtil';
import { APIPricingPlanTile } from '../../../../APIBuyer/Common/APIPricingPlanTile';
import {
  createPayload,
  createConsentPayload,
} from '../../../OrderAPI/Sub-components/ReviewTerms/ReviewTerms';
import { RESOURCE_TYPES } from '../../../../../constants/constants';
import { updateOrder } from '../../../../../redux/connectedComponent/orderAPI/orderAPI.slice';
import moment from 'moment';
import './APIPricingPlan.scss';
import { getInstanceIdVersionMap } from '../../../../../utils/utils';
import {
  AMPLITUDE_EVENTS,
  AMPLITUDE_PROPERTIES,
} from '../../../../../constants/amplitudeConstants';
import {
  getOrgAmplitudeProperties,
  sendAmplitudeData,
} from '../../../../../utils/amplitude';
import { trackManageApiIntegration } from '../../../../../utils/gtm';

const MAX_PLANS_ON_PAGE = 3;
const ACTIVE = 'active';
const PENDING = 'pending';
const RETIRED = 'retired';
const REST_API_TYPE = 'api';

function hasDatePassed(givenDateString) {
  const givenDate = new Date(givenDateString);
  const currentDate = new Date();
  return givenDate < currentDate;
}

export function formatDateToMMDDYYYY(isoDateString) {
  if (!isoDateString) return '';
  return moment(isoDateString).format('MM/DD/YYYY');
}

const getPricingPlanTileProps = ({
  plansToShow,
  selectedPlan,
  activePendingOrderIntegrationPlans,
  isAsync,
  firstDayOfNextMonth,
  lastDayOfNextMonth,
}) => {
  const { pendingIntegration, activeIntegration } =
    activePendingOrderIntegrationPlans;
  let planInfoObject = {};

  let apiPlansArr = plansToShow.map((plan) => {
    const isSelected = plan.id === selectedPlan;

    // Define conditions for integration existence (active or pending) with plan and retirement status
    const isPendingIntegration = pendingIntegration?.pricingPlanId === plan.id;
    const isActiveIntegration = activeIntegration?.pricingPlanId === plan.id;
    const isRetired = plan.status === RETIRED;

    let planStartDate = '';
    let planEndDate = '';
    let statusIndicatorLabel = '';

    // Handle planStartDate
    if (isPendingIntegration) {
      planStartDate = pendingIntegration?.integrationStartDate;
    } else if (isSelected) {
      planStartDate = isActiveIntegration ? '' : firstDayOfNextMonth;
    }

    // Handle planEndDate
    if (isSelected && isActiveIntegration) {
      planEndDate = '';
    } else if (!isSelected && isRetired && isActiveIntegration) {
      planEndDate = isActiveIntegration?.integrationEndDate;
    } else if (!isSelected && isActiveIntegration) {
      planEndDate = lastDayOfNextMonth;
    }

    const showExpiryDate =
      isRetired && planEndDate && !hasDatePassed(planEndDate);
    const showPlanStartDate =
      isSelected && planStartDate && !hasDatePassed(planStartDate);
    const formattedPlanEndDate = formatDateToMMDDYYYY(planEndDate);
    const formattedPlanStartDate = formatDateToMMDDYYYY(planStartDate);

    // Determine status indicator label
    if (isRetired) {
      statusIndicatorLabel =
        (formattedPlanEndDate && `Retired on ${formattedPlanEndDate}`) ||
        `Retired`;
    }

    if (isRetired && isActiveIntegration) {
      planInfoObject.retiredplanName = getPlanName(getPlanType(plan), false, plan?.name);
      planInfoObject.planEndDate = formattedPlanEndDate;
    } else if (isRetired && isSelected) {
      planInfoObject.retiredplanName = getPlanName(getPlanType(plan), false, plan?.name);
      planInfoObject.planEndDate = formattedPlanEndDate;
    } else if (isSelected) {
      planInfoObject.selectedplanName = getPlanName(getPlanType(plan), false, plan?.name);
      planInfoObject.planStartDate = formattedPlanStartDate;
    }

    return {
      key: plan.id,
      plan,
      isAsync,
      allEndpoints: [],
      instanceIdVersionMap: [],
      isSelected: isSelected && isRetired ? false : isSelected,
      isAPIinCart: true,
      isAPIContracted: false,
      isAPIPurchased: false,
      isRetired: isRetired,
      statusIndicatorLabel,
    };
  });

  let planInfoText = '',
    planInfoType = true;

  if (planInfoObject?.selectedplanName && planInfoObject?.planStartDate) {
    planInfoType = true;
    planInfoText = `The ${planInfoObject.selectedplanName} Plan will take effect at the start of your next billing cycle ${planInfoObject.planStartDate}.`;
  } else if (planInfoObject?.retiredplanName) {
    planInfoType = false;
    planInfoText = planInfoObject?.planEndDate
      ? `The ${planInfoObject.retiredplanName} Plan will retired on ${planInfoObject.planEndDate}.`
      : `The ${planInfoObject.retiredplanName} Plan is retired`;
  }
  return {
    apiPlansArr,
    planInfoText,
    planInfoType,
  };
};

export const APIPricingPlan = (props) => {
  const {
    title = '',
    apiId,
    isOpen,
    onClose,
    onAccept,
    selectedAPIEditPlanContext,
    pageTitle,
  } = props;

  const dispatch = useDispatch();
  const state = useSelector((state) => state);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [
    activePendingOrderIntegrationPlans,
    setActivePendingOrderIntegrationPlans,
  ] = useState({ pendingIntegration: {}, activeIntegration: {} });
  const [plansToShow, setPlansToShow] = useState([]);
  const [loading, setLoading] = useState(false);

  const apiName = selectedAPIEditPlanContext?.name;
  const integrations = selectedAPIEditPlanContext?.integrations;
  const { authenticationToken: token } = state?.loginDetailsSlice;
  const orgAmplitudeProperties = getOrgAmplitudeProperties(
    state?.loginDetailsSlice,
  );
  const { apiInfo, fetchingAPIDetails } = state?.apiDirectorySlice;
  const { updatingOrder, orders, orderUpdated, errorUpdatingOrder } =
    state?.orderAPISlice;
  const isAsync =
    selectedAPIEditPlanContext?.type !== REST_API_TYPE ? true : false;
  const pricingPlans = selectedAPIEditPlanContext?.pricingPlans;
  const selectedAPIId = selectedAPIEditPlanContext?.id;
  const isBundle =
    selectedAPIEditPlanContext?.type === RESOURCE_TYPES.BUNDLE ||
    orders?.bundleIntegrations?.some((bundle) => bundle?.id === selectedAPIId);
  const firstDayOfNextMonth = moment()
    .add(1, 'months')
    .startOf('month')
    .format('YYYY-MM-DD');
  const lastDayOfNextMonth = moment()
    .add(1, 'months')
    .startOf('month')
    .subtract(1, 'day')
    .format('YYYY-MM-DD');
  const [allEndpoints, setAllEndpoints] = useState([]);
  const [specVersions, setSpecVersions] = useState([]);

  useEffect(() => {
    if (apiInfo && !fetchingAPIDetails) {
      const spec = apiInfo?.data.environments?.[0]?.instances?.[0]?.spec;
      let endpoints = [];

      if (isAsync) {
        const channels = spec?.channels || {};
        endpoints = Object.keys(channels);
      } else {
        const paths = spec?.paths || {};
        Object.keys(paths).forEach((resource) => {
          const methods = Object.keys(paths[resource] || {});
          methods.forEach((method) => {
            endpoints.push(
              `${method.toUpperCase()} ${
                paths[resource][method]?.summary || ''
              }`,
            );
          });
        });
        setSpecVersions(getInstanceIdVersionMap(apiInfo.data));
      }

      setAllEndpoints(endpoints);
    }
  }, [apiInfo, fetchingAPIDetails]);

  // will effect on prop apiId
  useEffect(() => {
    if (loading && (orderUpdated || errorUpdatingOrder)) {
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  }, [loading, orderUpdated, errorUpdatingOrder]);

  useEffect(() => {
    if (integrations && Array.isArray(integrations)) {
      let pendingIntegration,
        activeIntegration,
        integrationPlan;
      
      let plansToShowUpdated = getPlansToShowForAPI(pricingPlans || []);

      // Get selected plan integrations
      integrations.forEach(({ pricingPlanId, endDate, startDate, status }) => {
        let integrationEndOn = moment(endDate);
        let currentMoment = moment();
        let isInfiniteEndDate = integrationEndOn.isSame('9999-12-31', 'day');

        // To show current selected plan from ACTIVE or PENDING integration
        if ((status === ACTIVE || status === PENDING) && isInfiniteEndDate) {
          integrationPlan = pricingPlanId;
        }

        //  Find ACTIVE or PENDING integrations
        if (integrationEndOn.isAfter(currentMoment) && pricingPlanId) {
          // Find the retired plan if it exists and add it in plan list to show as selected retired plan card
          const integratedRetiredPlan = plansToShowUpdated.find(
            (plan) => plan?.id === pricingPlanId && plan?.status === RETIRED,
          );
          if (integratedRetiredPlan) {
            plansToShowUpdated = [integratedRetiredPlan, ...plansToShowUpdated];
          }

          // prepare common integrationData
          const integrationData = {
            pricingPlanId,
            integrationEndDate: isInfiniteEndDate ? '' : endDate,
            integrationStartDate: startDate,
            integrationStatus: status,
            ...integratedRetiredPlan,
          };

          // Set active or pending integration based on status
          if (status === ACTIVE) {
            activeIntegration = integrationData;
          } else if (status === PENDING) {
            pendingIntegration = integrationData;
          }
        }
      });
      setPlansToShow(plansToShowUpdated);
      setSelectedPlan(integrationPlan);
      setActivePendingOrderIntegrationPlans({
        pendingIntegration,
        activeIntegration,
      });
    }
  }, [pricingPlans, integrations, apiId, isBundle]);


  const handlePricingPlanSelect = (planId) => {
    setSelectedPlan(planId);
  };
  const renderCustomHeader = () => {
    return (
      <div className="api-pricing-dialog-header">
        <div className="api-pricing-dialog-title">{title}</div>
        <DialogCloseButton
          dataTestId="header-close-button"
          className="api-pricing-dialog-close-btn"
          onClick={onClose}
        />
      </div>
    );
  };
  const renderCustomFooter = () => {
    const { pendingIntegration, activeIntegration } =
      activePendingOrderIntegrationPlans;
    return (
      <div className="api-pricing-dialog-footer">
        <Button
          dataTestId="footer-save-button"
          className="api-pricing-dialog-footer-btn"
          onClick={handleSaveEditPlan}
          isLoading={updatingOrder}
          size="standard"
          text="Save"
          variant={BUTTON_VARIANTS.PRIMARY}
          isDisabled={
            pendingIntegration?.pricingPlanId === selectedPlan ||
            activeIntegration?.pricingPlanId === selectedPlan
          }
        />
      </div>
    );
  };
  const handleSaveEditPlan = () => {
    let payload = createPayload([], orders);

    if (isBundle) {
      let newBundleIntegrations = [];
      if (
        payload.bundleIntegrations &&
        Array.isArray(payload.bundleIntegrations)
      ) {
        payload.bundleIntegrations.forEach((bundle = {}) => {
          if (bundle?.bundleId === selectedAPIId) {
            newBundleIntegrations.push({
              bundleId: bundle?.bundleId,
              pricePlanId: selectedPlan,
            });
          } else {
            newBundleIntegrations.push({ ...bundle });
          }
        });
      }

      setLoading(true);
      dispatch(
        updateOrder({
          payload: {
            ...payload,
            bundleIntegrations: newBundleIntegrations,
            consentData: {
              [selectedAPIId]: createConsentPayload(
                selectedAPIEditPlanContext?.consent?.documentLink ||
                  selectedAPIEditPlanContext?.consent?.signedTermsAndConditions,
                {
                  resourceType: RESOURCE_TYPES.BUNDLE,
                  resourceId: selectedAPIId,
                  resourcePricingPlanId: selectedPlan,
                },
                {}
              ),
            },
          },
          token,
          appId: orders?.app?.id,
        }),
      )
        .unwrap()
        .then(() => {
          // Store toast message in sessionStorage to display after page reload
          sessionStorage.setItem(
            'pricingPlanUpdated',
            JSON.stringify({
              status: true,
              apiName: selectedAPIEditPlanContext?.name,
              isBundle: true,
            }),
          );
          setLoading(false);
          if (onAccept) onAccept();
          if (onClose) onClose();
        })
        .catch(() => {
          setLoading(false);
        });
    } else {
      // Update apiPlanIds for regular APIs
      let newApiPlanIds = [];
      if (payload.apiPlanIds && Array.isArray(payload.apiPlanIds)) {
        payload.apiPlanIds.forEach((plan = {}) => {
          if (
            plan?.apiId === selectedAPIId ||
            plan?.apiId === `api-v2-${selectedAPIId}`
          ) {
            newApiPlanIds.push({
              apiId: plan?.apiId,
              planId: selectedPlan,
            });
          } else {
            newApiPlanIds.push({ ...plan });
          }
        });
      }

      setLoading(true);
      dispatch(
        updateOrder({
          payload: {
            ...payload,
            apiPlanIds: newApiPlanIds,
            consentData: {
              [selectedAPIId]: createConsentPayload(
                selectedAPIEditPlanContext?.consent?.documentLink,
                {
                  resourceType: selectedAPIEditPlanContext?.type,
                  resourceId: selectedAPIId,
                  resourcePricingPlanId: selectedPlan,
                },
              ),
            },
          },
          token,
          appId: orders?.app?.id,
        }),
      )
        .unwrap()
        .then(() => {
          // Store toast message in sessionStorage to display after page reload
          sessionStorage.setItem(
            'pricingPlanUpdated',
            JSON.stringify({
              status: true,
              apiName: selectedAPIEditPlanContext?.name,
              isBundle: false,
            }),
          );
          setLoading(false);
          if (onAccept) onAccept();
          if (onClose) onClose();
        })
        .catch(() => {
          setLoading(false);
        });
    }
    const newlySelectedPlanObj = plansToShow?.find((plan) => plan?.id === selectedPlan);
    const planSelectedName = newlySelectedPlanObj?.name || newlySelectedPlanObj?.plan_type || '';

    if (isBundle) {
      trackManageApiIntegration({
        actionType: 'Edit Pricing Plan',
        planSelected: planSelectedName,
        bundleId: selectedAPIId,
        bundleDisplayName: selectedAPIEditPlanContext?.name,
        isBundle: true,
      });
    } else {
      trackManageApiIntegration({
        actionType: 'Edit Pricing Plan',
        planSelected: planSelectedName,
        apiId: selectedAPIId,
        apiDisplayName: selectedAPIEditPlanContext?.name,
        apiType: isAsync ? 'Async API' : 'REST API',
        isBundle: false,
      });
    }

    sendAmplitudeData(
      AMPLITUDE_EVENTS.MY_APIS_EDIT_PRICING_PLAN_SAVE,
      orgAmplitudeProperties,
    );
  };

  let { apiPlansArr, planInfoText, planInfoType } = getPricingPlanTileProps({
    plansToShow,
    selectedPlan,
    activePendingOrderIntegrationPlans,
    isAsync,
    firstDayOfNextMonth,
    lastDayOfNextMonth,
  });

  return (
    <>
      <Dialog
        id="api-pricing"
        title={title}
        isOpen={isOpen}
        onClose={onClose}
        disableOverlayClicked={true}
        className="api-pricing-dialog"
        renderCustomFooterComponent={renderCustomFooter}
        customHeaderTag={renderCustomHeader}
      >
        <div className="api-pricing-dialog-body">
          <div className="api-pricing-dialog-plan-container-header">
            {apiName}
          </div>
          <div className="api-pricing-dialog-divider" />
          {planInfoText && (
            <Toast
              content={planInfoText}
              id="planInfo"
              variant={
                planInfoType ? TOAST_VARIANTS.NEUTRAL : TOAST_VARIANTS.WARNING
              }
            />
          )}

          <div
            className={`api-pricing-dialog-plans-container ${
              plansToShow?.length < MAX_PLANS_ON_PAGE ? 'centered' : ''
            }`}
          >
            {apiPlansArr.map((tileProps) => (
              <div style={{ margin: '12px' }} key={tileProps.key}>
                <APIPricingPlanTile
                  {...tileProps}
                  selectPricingPlan={handlePricingPlanSelect}
                  allEndpoints={allEndpoints}
                  instanceIdVersionMap={specVersions}
                  isBundle={isBundle}
                />
              </div>
            ))}
          </div>
        </div>
      </Dialog>
    </>
  );
};
